import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';

import {
  Field,
} from 'formik';

import {
  ContainerBottom,
  ContainerHeader,
  CustomIcon,
  DefaultSurveyLabel,
  Dropdown,
  ImageIcon,
  Input,
  ItemContent,
  SkipLogicContainer,
  SkipLogicIcon,
  SkipLogicLabel,
  SurveyLabelContainer,
  SurveyPageTitle,
  TaskSegment,
  ThreeDotContainer,
} from './styles';

import QuestionsForm from '../../../../components/questions-form';
import SkipLogicModal from './skip-logic-modal';
import ConfirmModal from '../../../../../core/components/confirm-modal';

const endPageOption = {
  key: 'end_screen',
  value: 'end_screen',
  text: 'End screen',
};

const SurveyPage = (props) => {
  /** props */
  const {
    errors,
    index,
    isEdit,
    onClickOpenModalMoveTask,
    onDeletePage,
    onDuplicatePage,
    onDuplicateQuestion,
    onNewQuestion,
    onQuestionsSortEnd,
    onRemoveQuestion,
    pagesList,
    setFieldValue,
    touched,
    values,
    hideTrashCan,
  } = props;

  const {
    pageName,
    tasks,
  } = values;

  const testType = 'survey';

  /** states */
  const [showSkipLogic, setShowSkipLogic] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const skipToOptions = useMemo(() => {
    // next pages only
    const nextPages = _.drop(pagesList, index + 1);

    const options = _.map(nextPages, (item) => ({
      key: item.id,
      value: item.id,
      text: item.pageName,
    }));

    return [...options, endPageOption];
  }, [pagesList, index]);

  const handleNewQuestion = (key) => {
    onNewQuestion(index, key);
  };

  const handleDuplicateQuestion = (key) => {
    onDuplicateQuestion(index, key);
  };

  const handleRemoveQuestion = (key) => {
    onRemoveQuestion(index, key);
  };

  const handleOpenSkipLogic = () => {
    setShowSkipLogic(true);
  };

  const handleCloseSkipLogic = () => {
    setShowSkipLogic(false);
  };

  const handleDeletePage = () => {
    onDeletePage();
    setShowDeleteModal(false);
  };

  const handleMoveQuestion = (element) => {
    onQuestionsSortEnd(element, index);
  };

  const handleTitleChange = (e) => {
    if (!_.isEmpty(e)) {
      setFieldValue(`surveysPages[${index}].pageName`, e.target.value);
    }
  };

  const handleAddSkipLogic = (skipLogic) => {
    setFieldValue(`surveysPages[${index}].skipLogic`, skipLogic);
  };

  const buildButtonLabel = () => {
    if (values.skipLogic && !_.isEmpty(values.skipLogic) && !_.isEmpty(values.skipLogic.conditions)) {
      return 'Edit skip logic';
    }

    return 'Skip logic';
  };

  return (
    <TaskSegment>
      <ContainerHeader>
        {showDeleteModal && (
          <ConfirmModal
            active={showDeleteModal}
            onClose={() => setShowDeleteModal(false)}
            onSubmit={handleDeletePage}
          />
        )}

        {showSkipLogic && (
          <SkipLogicModal
            active={showSkipLogic}
            onClose={handleCloseSkipLogic}
            page={values}
            skipToOptions={skipToOptions}
            onSetValue={handleAddSkipLogic}
          />
        )}

        <SurveyPageTitle>
          {isEdit ? `PAGE ${values.order}` : `PAGE ${index + 1}`}
        </SurveyPageTitle>

        {!isEdit && (
          <ThreeDotContainer>
            <Dropdown
              item
              trigger={(
                <CustomIcon>
                  <ImageIcon src="/images/dots-three-icon.png" />
                </CustomIcon>
              )}
            >
              <Dropdown.Menu direction="left">
                <Dropdown.Item onClick={onDuplicatePage}>
                  <ItemContent>
                    Duplicate Page
                  </ItemContent>
                </Dropdown.Item>
                <Dropdown.Item onClick={onClickOpenModalMoveTask}>
                  <ItemContent>
                    Move Page
                  </ItemContent>
                </Dropdown.Item>
                <Dropdown.Item onClick={() => setShowDeleteModal(true)}>
                  <ItemContent errorColor>
                    Delete Page
                  </ItemContent>
                </Dropdown.Item>

              </Dropdown.Menu>
            </Dropdown>
          </ThreeDotContainer>
        )}

      </ContainerHeader>

      {!isEdit && (
        <Field
          value={pageName}
          component={Input}
          onChange={handleTitleChange}
        />
      )}

      {(tasks && tasks.length === 0) && (
        <>
          <SurveyLabelContainer>
            <DefaultSurveyLabel>Click &quot;Add new question&quot; to build your survey.</DefaultSurveyLabel>
          </SurveyLabelContainer>
        </>
      )}

      <ContainerBottom direction={!_.isEmpty(tasks) ? 'column' : 'row'}>
        <QuestionsForm
          values={values}
          errors={errors}
          touched={touched}
          handleSortOnEnd={handleMoveQuestion}
          handleNewQuestion={handleNewQuestion}
          handleRemoveQuestion={handleRemoveQuestion}
          onClickDuplicate={handleDuplicateQuestion}
          taskIndex={index}
          setFieldValue={setFieldValue}
          testType={testType}
          small
          isEdit={isEdit}
          hideTrashCan={hideTrashCan}
        />

        {!isEdit && (
          <SkipLogicContainer onClick={handleOpenSkipLogic}>
            <SkipLogicIcon name="code branch" />
            <SkipLogicLabel>
              {buildButtonLabel()}
            </SkipLogicLabel>
          </SkipLogicContainer>
        )}

      </ContainerBottom>

    </TaskSegment>
  );
};

SurveyPage.propTypes = {
  setFieldValue: PropTypes.func.isRequired,
  index: PropTypes.number.isRequired,
  onNewQuestion: PropTypes.func,
  onQuestionsSortEnd: PropTypes.func,
  onDeletePage: PropTypes.func,
  onDuplicatePage: PropTypes.func,
  onDuplicateQuestion: PropTypes.func,
  onRemoveQuestion: PropTypes.func,
  errors: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.shape()),
    PropTypes.shape({
      surveysPages: PropTypes.arrayOf(PropTypes.shape({
        tasks: PropTypes.arrayOf(PropTypes.shape({
          label: PropTypes.string,
        })),
      })),
    }),
  ]).isRequired,
  touched: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.shape()),
    PropTypes.shape({
      surveysPages: PropTypes.arrayOf(PropTypes.shape({
        tasks: PropTypes.arrayOf(PropTypes.shape({
          label: PropTypes.bool,
        })),
      })),
    }),
  ]).isRequired,
  onClickOpenModalMoveTask: PropTypes.func,
  values: PropTypes.shape({
    order: PropTypes.number.isRequired,
    pageName: PropTypes.string.isRequired,
    tasks: PropTypes.arrayOf(PropTypes.shape()).isRequired,
    skipLogic: PropTypes.oneOfType([PropTypes.object]),
  }),
  isEdit: PropTypes.bool,
  pagesList: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      pageName: PropTypes.string.isRequired,
    }),
  ),
  hideTrashCan: PropTypes.bool,
};

SurveyPage.defaultProps = {
  values: {},
  isEdit: false,
  hideTrashCan: false,
  onClickOpenModalMoveTask: () => { },
  onQuestionsSortEnd: null,
  onNewQuestion: () => { },
  onDeletePage: () => { },
  onDuplicatePage: () => { },
  onDuplicateQuestion: () => { },
  onRemoveQuestion: () => { },
  pagesList: [],
};

export default SurveyPage;
